import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, LogOut, Lock, ImagePlus, Star, Trash2, Images,
  GripVertical, ChevronLeft,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const move = (arr, from, to) => {
  const copy = [...arr];
  const [item] = copy.splice(from, 1);
  copy.splice(to, 0, item);
  return copy;
};

export default function AdminPage() {
  const [user, setUser] = useState(null); // null = checking, false = signed out, object = signed in
  const [enquiries, setEnquiries] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  async function loadData() {
    let res = await fetch(`${API}/admin/inquiries`, { credentials: "include" });
    if (res.status === 401) {
      const r = await fetch(`${API}/auth/refresh`, { method: "POST", credentials: "include" });
      if (r.ok) res = await fetch(`${API}/admin/inquiries`, { credentials: "include" });
    }
    if (res.ok) {
      setEnquiries(await res.json());
      setUser(true);
      return true;
    }
    setUser(false);
    return false;
  }

  async function loadPhotos() {
    try {
      const res = await fetch(`${API}/media`);
      if (res.ok) setPhotos(await res.json());
    } catch {
      setPhotos([]);
    }
  }

  useEffect(() => {
    loadData().then(loadPhotos);
  }, []);

  async function onLogin(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(formatApiErrorDetail(data.detail));
      toast.success("Welcome back.");
      await loadData();
      await loadPhotos();
    } catch (err) {
      toast.error(err.message || "Sign in failed.");
    } finally {
      setBusy(false);
      setPassword("");
    }
  }

  async function onLogout() {
    await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" });
    setUser(false);
    setEnquiries([]);
    setPhotos([]);
  }

  async function onUpload(e) {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const res = await fetch(`${API}/admin/media`, {
          method: "POST",
          credentials: "include",
          body: fd,
        });
        const data = await res.json();
        if (!res.ok) throw new Error(formatApiErrorDetail(data.detail));
      }
      await loadPhotos();
      toast.success(`${files.length} ${files.length === 1 ? "photo" : "photos"} uploaded — now live on the listing.`);
    } catch (err) {
      toast.error(err.message || "Upload failed.");
    } finally {
      setUploading(false);
    }
  }

  async function setHero(id) {
    const res = await fetch(`${API}/admin/media/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ role: "hero" }),
    });
    if (res.ok) {
      await loadPhotos();
      toast.success("Hero photo updated — the page now opens with it.");
    } else {
      toast.error("Could not set the hero photo.");
    }
  }

  async function saveCaption(id, caption) {
    const res = await fetch(`${API}/admin/media/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ caption }),
    });
    if (res.ok) {
      setPhotos((ps) => ps.map((p) => (p.id === id ? { ...p, caption } : p)));
      toast.success(caption ? `Caption saved: “${caption}”` : "Caption cleared.");
    } else {
      toast.error("Could not save the caption.");
    }
  }

  async function persistOrder(newPhotos) {
    setPhotos(newPhotos);
    const res = await fetch(`${API}/admin/media/reorder`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ order: newPhotos.map((p) => p.id) }),
    });
    if (res.ok) toast.success("Gallery order saved.");
    else {
      toast.error("Could not save the new order.");
      loadPhotos();
    }
  }

  function onDrop(index) {
    if (dragIndex === null || dragIndex === index) {
      setDragIndex(null);
      return;
    }
    persistOrder(move(photos, dragIndex, index));
    setDragIndex(null);
  }

  async function deletePhoto(id) {
    const res = await fetch(`${API}/admin/media/${id}`, { method: "DELETE", credentials: "include" });
    if (res.ok) {
      await loadPhotos();
      toast.success("Photo removed from the listing.");
    } else {
      toast.error("Could not remove the photo.");
    }
  }

  if (user === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0B0C0E]">
        <Loader2 size={28} className="animate-spin text-bronze" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F4F4F3] p-4 dark:bg-[#101114]">
        <form
          onSubmit={onLogin}
          data-testid="admin-login-form"
          className="w-full max-w-sm rounded-3xl border border-zinc-200 bg-white p-8 shadow-xl dark:border-zinc-800 dark:bg-zinc-900"
        >
          <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-bronze/10 text-bronze">
            <Lock size={20} />
          </span>
          <h1 className="mt-4 font-display text-2xl font-bold text-zinc-900 dark:text-white">
            Enquiries Admin
          </h1>
          <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
            Sign in with the listing account.
          </p>
          <div className="mt-6 space-y-4">
            <input
              type="email"
              required
              data-testid="admin-login-email-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email"
              aria-label="Email"
              className="w-full rounded-xl border border-zinc-200 bg-white px-4 py-3 text-sm text-zinc-900 outline-none transition focus:border-bronze focus:ring-2 focus:ring-bronze/30 dark:border-zinc-700 dark:bg-zinc-900 dark:text-white"
            />
            <input
              type="password"
              required
              data-testid="admin-login-password-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              aria-label="Password"
              className="w-full rounded-xl border border-zinc-200 bg-white px-4 py-3 text-sm text-zinc-900 outline-none transition focus:border-bronze focus:ring-2 focus:ring-bronze/30 dark:border-zinc-700 dark:bg-zinc-900 dark:text-white"
            />
            <button
              type="submit"
              data-testid="admin-login-submit-button"
              disabled={busy}
              className="flex w-full items-center justify-center gap-2 rounded-full bg-zinc-900 px-6 py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-bronze disabled:opacity-60 dark:bg-white dark:text-black dark:hover:bg-bronze dark:hover:text-white"
            >
              {busy ? <Loader2 size={16} className="animate-spin" /> : "Sign In"}
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA] dark:bg-[#0B0C0E]">
      <header className="border-b border-zinc-200 bg-white/80 backdrop-blur-xl dark:border-zinc-800 dark:bg-black/70">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6">
          <p className="font-display text-sm font-extrabold tracking-[0.28em] text-zinc-900 dark:text-white">
            461 BEECHWOOD — <span className="text-bronze">ADMIN</span>
          </p>
          <button
            onClick={onLogout}
            data-testid="admin-logout-button"
            className="flex items-center gap-2 rounded-full border border-zinc-300 px-4 py-2 text-sm font-medium text-zinc-700 transition-colors duration-200 hover:border-zinc-900 dark:border-zinc-600 dark:text-zinc-200 dark:hover:border-white"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
        <section data-testid="admin-photos-section">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <h2 className="flex items-center gap-2 font-display text-xl font-bold text-zinc-900 dark:text-white">
                <Images size={18} className="text-bronze" /> Listing Photos
              </h2>
              <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
                Drag a photo to reorder the gallery, add a caption (like “Kitchen”) for the
                lightbox, and pick one as the hero.
              </p>
            </div>
            <label
              data-testid="admin-photo-upload-label"
              className="flex cursor-pointer items-center gap-2 rounded-full bg-bronze px-5 py-2.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-bronze-dark"
            >
              {uploading ? <Loader2 size={15} className="animate-spin" /> : <ImagePlus size={15} />}
              {uploading ? "Uploading…" : "Upload Photos"}
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                multiple
                data-testid="admin-photo-input"
                className="hidden"
                onChange={onUpload}
                disabled={uploading}
              />
            </label>
          </div>

          <div
            data-testid="admin-photos-grid"
            className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4"
          >
            {photos.length === 0 && (
              <div className="col-span-full rounded-2xl border border-dashed border-zinc-300 p-10 text-center text-sm text-zinc-500 dark:border-zinc-700 dark:text-zinc-400">
                No photos uploaded yet — the site is showing sample imagery.
              </div>
            )}
            {photos.map((p, i) => (
              <div
                key={p.id}
                data-testid={`admin-photo-card-${p.id}`}
                draggable
                onDragStart={(e) => setDragIndex(i)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => onDrop(i)}
                onDragEnd={() => setDragIndex(null)}
                className={`overflow-hidden rounded-2xl border bg-white transition-opacity dark:bg-zinc-900 ${
                  dragIndex === i
                    ? "border-bronze opacity-50"
                    : "border-zinc-200 dark:border-zinc-800"
                }`}
              >
                <div className="relative aspect-[4/3] cursor-grab active:cursor-grabbing">
                  <img src={p.url} alt={p.caption || p.original_filename} className="h-full w-full object-cover" draggable={false} />
                  {p.role === "hero" && (
                    <span className="absolute left-2 top-2 flex items-center gap-1 rounded-full bg-bronze px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-white">
                      <Star size={10} /> Hero
                    </span>
                  )}
                  <span
                    data-testid={`admin-photo-drag-handle-${p.id}`}
                    title="Drag to reorder"
                    className="absolute bottom-2 right-2 rounded-full bg-black/55 p-1.5 text-white backdrop-blur-sm"
                  >
                    <GripVertical size={13} />
                  </span>
                  {i > 0 && (
                    <button
                      onClick={() => persistOrder(move(photos, i, i - 1))}
                      data-testid={`admin-photo-move-earlier-${p.id}`}
                      aria-label="Move earlier"
                      title="Move earlier"
                      className="absolute bottom-2 left-2 rounded-full bg-black/55 p-1.5 text-white backdrop-blur-sm transition-colors duration-200 hover:bg-black/75"
                    >
                      <ChevronLeft size={13} />
                    </button>
                  )}
                </div>
                <div className="p-3">
                  <input
                    data-testid={`admin-photo-caption-input-${p.id}`}
                    defaultValue={p.caption || ""}
                    placeholder="Caption — e.g. Kitchen"
                    aria-label={`Caption for ${p.original_filename}`}
                    onBlur={(e) => {
                      const val = e.target.value.trim();
                      if (val !== (p.caption || "")) saveCaption(p.id, val);
                    }}
                    className="w-full rounded-lg border border-zinc-200 bg-white px-3 py-1.5 text-xs text-zinc-900 outline-none transition focus:border-bronze focus:ring-2 focus:ring-bronze/30 dark:border-zinc-700 dark:bg-zinc-900 dark:text-white"
                  />
                  <div className="mt-2 flex items-center justify-between gap-2">
                    <p className="truncate text-xs text-zinc-500 dark:text-zinc-400">{p.original_filename}</p>
                    <div className="flex flex-none gap-1.5">
                      {p.role !== "hero" && (
                        <button
                          onClick={() => setHero(p.id)}
                          data-testid={`admin-photo-hero-button-${p.id}`}
                          aria-label="Set as hero"
                          title="Set as hero"
                          className="rounded-full border border-zinc-200 p-1.5 text-zinc-500 transition-colors duration-200 hover:border-bronze hover:text-bronze dark:border-zinc-700 dark:text-zinc-300"
                        >
                          <Star size={13} />
                        </button>
                      )}
                      <button
                        onClick={() => deletePhoto(p.id)}
                        data-testid={`admin-photo-delete-button-${p.id}`}
                        aria-label="Delete photo"
                        title="Delete"
                        className="rounded-full border border-zinc-200 p-1.5 text-zinc-500 transition-colors duration-200 hover:border-red-500 hover:text-red-500 dark:border-zinc-700 dark:text-zinc-300"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-14" data-testid="admin-enquiries-section">
          <h2 className="font-display text-xl font-bold text-zinc-900 dark:text-white">Enquiries</h2>
          <p data-testid="admin-enquiries-count" className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
            {enquiries.length} {enquiries.length === 1 ? "enquiry" : "enquiries"} received
          </p>
          <div data-testid="admin-enquiries-table" className="mt-6 space-y-4">
            {enquiries.length === 0 && (
              <div className="rounded-2xl border border-dashed border-zinc-300 p-10 text-center text-sm text-zinc-500 dark:border-zinc-700 dark:text-zinc-400">
                No enquiries yet — they'll appear here the moment a visitor writes.
              </div>
            )}
            {enquiries.map((q) => (
              <div
                key={q.id}
                data-testid={`admin-enquiry-card-${q.id}`}
                className="rounded-2xl border border-zinc-200 bg-white p-6 dark:border-zinc-800 dark:bg-zinc-900"
              >
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="font-display text-lg font-semibold text-zinc-900 dark:text-white">
                    {q.name}
                  </p>
                  <p className="text-xs uppercase tracking-[0.15em] text-zinc-500 dark:text-zinc-400">
                    {String(q.created_at).slice(0, 16).replace("T", " ")}
                  </p>
                </div>
                <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-sm text-zinc-600 dark:text-zinc-300">
                  <span>{q.email}</span>
                  {q.phone && <span>{q.phone}</span>}
                  <span className="rounded-full bg-bronze/10 px-3 py-0.5 text-xs font-semibold text-bronze">
                    {q.buyer_type}
                  </span>
                  {q.preferred_date && <span>Tour: {q.preferred_date}</span>}
                </div>
                {q.message && (
                  <p className="mt-3 border-t border-zinc-100 pt-3 text-sm leading-relaxed text-zinc-700 dark:border-zinc-800 dark:text-zinc-300">
                    {q.message}
                  </p>
                )}
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}
